/**
 * GSAP Animations
 * A centralized system for applying animations throughout the site
 * using GSAP and data attributes.
 */

// Add CSS to hide animated elements initially (prevent FOUC)
// Skip this in Shopify design mode to keep elements visible
if (!(window.Shopify && Shopify.designMode)) {
  const style = document.createElement('style');
  style.textContent = '[data-animate] { visibility: hidden; }';
  document.head.appendChild(style);
}

// Animation Registry - Centralized configuration for all animation types
const ANIMATION_REGISTRY = {
  // Basic animations
  'fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  'fade-up': {
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 }
  },
  'fade-down': {
    from: { opacity: 0, y: -20 },
    to: { opacity: 1, y: 0 }
  },
  'fade-left': {
    from: { opacity: 0, x: -20 },
    to: { opacity: 1, x: 0 }
  },
  'fade-right': {
    from: { opacity: 0, x: 20 },
    to: { opacity: 1, x: 0 }
  },

  // Zoom animations
  'zoom-in': {
    from: { opacity: 0, scale: 0.9 },
    to: { opacity: 1, scale: 1 }
  },
  'zoom-out': {
    from: { opacity: 0, scale: 1.1 },
    to: { opacity: 1, scale: 1 }
  },

  // Filter animations
  'blur': {
    from: { opacity: 0, filter: 'blur(6px)' },
    to: { opacity: 1, filter: 'blur(0px)' }
  },
  'blur-up': {
    from: { opacity: 0, filter: 'blur(6px)', y: 20 },
    to: { opacity: 1, filter: 'blur(0px)', y: 0 }
  },

  // 3D animations
  'flip-x': {
    from: { opacity: 0, rotationX: 90 },
    to: { opacity: 1, rotationX: 0 }
  },
  'flip-y': {
    from: { opacity: 0, rotationY: 90 },
    to: { opacity: 1, rotationY: 0 }
  }
};

// Details animation configurations
const DETAILS_ANIMATIONS = {
  'blur-fade': {
    open: {
      from: { opacity: 0, filter: 'blur(6px)' },
      to: { opacity: 1, filter: 'blur(0px)' }
    },
    close: {
      to: { opacity: 0, filter: 'blur(6px)' }
    }
  },
  'blur-up': {
    open: {
      from: { opacity: 0, filter: 'blur(6px)', y: 10 },
      to: { opacity: 1, filter: 'blur(0px)', y: 0 }
    },
    close: {
      to: { opacity: 0, filter: 'blur(6px)', y: 10 }
    }
  },
  'blur-down': {
    open: {
      from: { opacity: 0, filter: 'blur(6px)', y: -10 },
      to: { opacity: 1, filter: 'blur(0px)', y: 0 }
    },
    close: {
      to: { opacity: 0, filter: 'blur(6px)', y: -10 }
    }
  }
};

// Default animation options
const DEFAULT_OPTIONS = {
  duration: 0.8,
  delay: 0,
  easing: 'power2.out',
  threshold: 0.1,
  once: true
};

// Default details animation options
const DEFAULT_DETAILS_OPTIONS = {
  duration: 0.4,
  easing: 'power2.out',
  type: 'blur-fade'
};

let hasInitializedOnce = false;
const DEBUG_ANIMATIONS = typeof Shopify !== 'undefined' && Shopify.designMode;

function debugLog(...args) {
  if (!DEBUG_ANIMATIONS) return;
  console.log(...args);
}

let navigationReadyController = null;
let navigationVisibilityHandler = null;
let navigationReadyFallbackId = null;

function createNavigationReadyController() {
  let resolved = false;
  let resolveFn = () => {};

  const promise = new Promise(resolve => {
    resolveFn = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };
  });

  return {
    get resolved() {
      return resolved;
    },
    resolve: resolveFn,
    promise
  };
}

function cleanupNavigationReadyGuards() {
  if (navigationVisibilityHandler) {
    document.removeEventListener('visibilitychange', navigationVisibilityHandler);
    navigationVisibilityHandler = null;
  }
  if (navigationReadyFallbackId) {
    clearTimeout(navigationReadyFallbackId);
    navigationReadyFallbackId = null;
  }
}

function resolveNavigationReady() {
  if (!navigationReadyController || navigationReadyController.resolved) return;
  cleanupNavigationReadyGuards();
  navigationReadyController.resolve();
}

function resetNavigationReady() {
  cleanupNavigationReadyGuards();
  navigationReadyController = createNavigationReadyController();

  if (document.visibilityState === 'visible') {
    requestAnimationFrame(resolveNavigationReady);
  } else {
    navigationVisibilityHandler = () => {
      resolveNavigationReady();
    };
    document.addEventListener('visibilitychange', navigationVisibilityHandler, { once: true });

    navigationReadyFallbackId = setTimeout(() => {
      resolveNavigationReady();
    }, 240);
  }
}

function onNavigationReady(callback) {
  if (!navigationReadyController) {
    resetNavigationReady();
  }

  navigationReadyController.promise.then(() => {
    requestAnimationFrame(() => {
      callback();
    });
  });
}

resetNavigationReady();

const supportsPageSwap = 'pageswap' in window;

/**
 * Check if GSAP is ready
 */
function isGSAPReady() {
  return typeof gsap !== 'undefined' &&
         typeof ScrollTrigger !== 'undefined' &&
         typeof SplitText !== 'undefined';
}

/**
 * Convert CSS easing to GSAP easing
 */
function convertEasing(cssEasing) {
  const easingMap = {
    'ease': 'power1.inOut',
    'ease-in': 'power2.in',
    'ease-out': 'power2.out',
    'ease-in-out': 'power2.inOut',
    'linear': 'none'
  };
  return easingMap[cssEasing] || cssEasing;
}

/**
 * Get animation options from data attributes
 */
function getOptionsFromAttributes(element, defaultOptions = DEFAULT_OPTIONS) {
  const rawEasing = element.getAttribute('data-animate-easing') || defaultOptions.easing;

  return {
    duration: parseFloat(element.getAttribute('data-animate-duration') || defaultOptions.duration),
    delay: parseFloat(element.getAttribute('data-animate-delay') || defaultOptions.delay),
    easing: convertEasing(rawEasing),
    threshold: (window.Shopify && Shopify.designMode) ? 0 : parseFloat(element.getAttribute('data-animate-threshold') || defaultOptions.threshold),
    // In theme editor, allow retriggering during edits
    once: (window.Shopify && Shopify.designMode) ? false : element.getAttribute('data-animate-once') !== 'false'
  };
}

/**
 * Apply scroll animation to an element
 */
function applyScrollAnimation(element, type, options) {
  const config = ANIMATION_REGISTRY[type] || ANIMATION_REGISTRY['fade-in'];

  // Get element with data-animate (might be the element itself or a parent)
  const animatedElement = element.hasAttribute('data-animate') ? element : element.closest('[data-animate]');

  // Make visible now that animation is set up
  if (animatedElement) {
    animatedElement.style.visibility = 'visible';
  }

  // Set initial state
  gsap.set(element, config.from);
  
  // Hint browser for GPU acceleration
  element.style.willChange = 'opacity, transform, filter';

  // Create ScrollTrigger animation
  ScrollTrigger.create({
    trigger: element,
    start: `top ${100 - (options.threshold * 100)}%`,
    once: options.once,
    onEnter: () => {
      const runAnimation = () => {
        gsap.to(element, {
          ...config.to,
          duration: options.duration,
          delay: options.delay,
          ease: options.easing,
          force3D: true,
          onComplete: () => {
            // Clear will-change after animation to let browser reclaim resources
            element.style.willChange = 'auto';
          }
        });
      };

      if (document.visibilityState === 'visible') {
        runAnimation();
      } else {
        onNavigationReady(runAnimation);
      }
    }
  });
}

/**
 * Apply text animation with SplitText for headings
 */
function applyTextAnimation(element, type, options, animateType = 'words') {
  // Lazy-load SplitText only when we actually need to split text
  if (typeof SplitText === 'undefined' && window.SplitText) {
    debugLog('Lazy-loading SplitText plugin');
    SplitText = window.SplitText;
    gsap.registerPlugin(SplitText);
  }

  const config = ANIMATION_REGISTRY[type] || ANIMATION_REGISTRY['fade-in'];

  // Get the parent element with data-animate attribute
  const animatedParent = element.closest('[data-animate]');

  try {
    debugLog('Splitting text for:', element, 'text:', element.textContent, 'animate-type:', animateType);

    let elementsToAnimate;

    if (animateType === 'lines') {
      // Check if the element already has .split-title-line children
      const lineSpans = element.querySelectorAll('.split-title-line');

      if (lineSpans.length > 0) {
        debugLog('Found pre-split lines:', lineSpans.length, 'elements:', lineSpans);
        elementsToAnimate = Array.from(lineSpans);

        // Log each line to debug
        elementsToAnimate.forEach((line, i) => {
          debugLog(`Line ${i}:`, line.textContent, 'classes:', line.className);
        });
      } else {
        // Fallback to SplitText if no pre-split lines found
        debugLog('Element HTML before split:', element.innerHTML);
        debugLog('Element text content:', element.textContent);
        
        // Normalize HTML structure for consistent splitting
        const originalHTML = element.innerHTML;
        
        // Replace <br> tags with line breaks and remove all HTML tags
        let normalizedText = originalHTML
          .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to line breaks
          .replace(/<[^>]*>/g, '')         // Remove all HTML tags
          .replace(/\n\s*\n/g, '\n')      // Remove empty lines
          .trim();
        
        debugLog('Normalized text:', normalizedText);
        
        // If we have line breaks, split manually for consistency
        if (normalizedText.includes('\n')) {
          const lines = normalizedText.split('\n').filter(line => line.trim());
          debugLog('Manual split by line breaks:', lines.length, 'lines');
          
          // Create clean spans for each line
          element.innerHTML = lines.map(line => 
            `<span class="split-line">${line.trim()}</span>`
          ).join('');
          
          elementsToAnimate = element.querySelectorAll('.split-line');
          debugLog('Manual split created:', elementsToAnimate.length, 'lines');
        } else {
          // Use SplitText for text without line breaks
          const split = new SplitText(element, { 
            type: 'lines',
            linesClass: 'split-line',
            absolute: false,
            reduceWhiteSpace: true,
            deepSlice: true,
            smartWrap: true
          });
          elementsToAnimate = split.lines;
          debugLog('SplitText created:', elementsToAnimate.length, 'lines');
        }
        
        // Always clean up any unwanted HTML structure (runs for both manual and SplitText)
        elementsToAnimate.forEach(line => {
          debugLog('Cleaning up line:', line.innerHTML);
          
          // Get the text content and replace the entire innerHTML
          const textContent = line.textContent.trim();
          line.innerHTML = textContent;
          
          debugLog('After cleanup:', line.innerHTML);
        });
        
        // Update elementsToAnimate to reflect any changes
        elementsToAnimate = element.querySelectorAll('.split-line');
        
        debugLog('Final result:', elementsToAnimate.length, 'lines');
      }
    } else {
      // Default to words
      const split = new SplitText(element, { 
        type: 'words',
        wordsClass: 'split-word',
        absolute: false,
        reduceWhiteSpace: true,
        deepSlice: false,
        smartWrap: false
      });
      elementsToAnimate = split.words;
      debugLog('Split into words:', elementsToAnimate?.length);
    }

    if (!elementsToAnimate || elementsToAnimate.length === 0) {
      console.warn('No elements found after split');
      // Fallback to standard animation if splitting fails
      applyScrollAnimation(element, type, options);
      return;
    }

    // Make parent visible now that we've split the text
    if (animatedParent) {
      animatedParent.style.visibility = 'visible';
    }

    // Set initial state for all elements
    gsap.set(elementsToAnimate, config.from);
    debugLog('Set initial state:', config.from, 'for', elementsToAnimate.length, 'elements');

    if (DEBUG_ANIMATIONS) {
      // Log the computed styles to verify initial state was set
      elementsToAnimate.forEach((el, i) => {
        const styles = window.getComputedStyle(el);
        console.log(`Element ${i} initial state - opacity:`, styles.opacity, 'filter:', styles.filter, 'transform:', styles.transform);
      });
    }

    // Determine stagger amount based on type
    const staggerAmount = animateType === 'lines' ? 0.15 : 0.03; // 150ms for lines, 30ms for words

    // Create ScrollTrigger animation with stagger
    ScrollTrigger.create({
      trigger: element,
      start: `top ${100 - (options.threshold * 100)}%`,
      once: options.once,
      onEnter: () => {
        const runAnimation = () => {
          debugLog(`ScrollTrigger fired! Animating ${elementsToAnimate.length} ${animateType}`);
          gsap.to(elementsToAnimate, {
            ...config.to,
            duration: options.duration,
            delay: options.delay,
            stagger: staggerAmount,
            ease: options.easing,
            force3D: true,
            onStart: () => {
              debugLog('Animation started');
            },
            onComplete: () => {
              debugLog('Animation completed');
            }
          });
        };

        if (document.visibilityState === 'visible') {
          runAnimation();
        } else {
          onNavigationReady(runAnimation);
        }
      }
    });
  } catch (error) {
    console.warn('SplitText failed for element:', element, error);
    // Fallback to standard animation
    applyScrollAnimation(element, type, options);
  }
}

/**
 * Get the actual text element to animate
 * If the element with data-animate contains a heading, animate the heading instead
 */
function getTextElement(element) {
  // Check if element has a single heading child (common pattern in your theme)
  const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
  if (headings.length === 1) {
    return headings[0];
  }

  // Check if element itself is a heading or inline text element
  const tag = element.tagName?.toLowerCase();
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a'].includes(tag)) {
    return element;
  }

  // Check if element has text content without complex block children
  const hasComplexChildren = Array.from(element.children).some(child => {
    const childTag = child.tagName?.toLowerCase();
    return ['div', 'section', 'article', 'ul', 'ol'].includes(childTag);
  });

  if (!hasComplexChildren && element.textContent?.trim()) {
    return element;
  }

  return null;
}

/**
 * Check if element contains text that should be split
 */
function shouldSplitText(element) {
  return getTextElement(element) !== null;
}

/**
 * Initialize scroll animations for all animated elements
 */
function initScrollAnimations() {
  const elements = document.querySelectorAll('[data-animate]');
  debugLog(`Found ${elements.length} elements with data-animate`);

  // Optimized: use larger rootMargin to start animating earlier, reducing perceived delay
  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      const animationType = element.getAttribute('data-animate');
      const animateTypeAttr = element.getAttribute('data-animate-type');
      const shouldSplit = !!animateTypeAttr && animateTypeAttr !== 'none';
      const animateType = animateTypeAttr || 'words';
      const options = getOptionsFromAttributes(element);

      debugLog('Element in view, starting animation:', {
        element,
        animationType,
        shouldSplit,
        visible: entry.isIntersecting,
        intersectionRatio: entry.intersectionRatio
      });

      // Get the actual text element (might be a child heading)
      const textElement = getTextElement(element);

      if (shouldSplit && textElement) {
        applyTextAnimation(textElement, animationType, options, animateType);
      } else {
        applyScrollAnimation(element, animationType, options);
      }

      observer.unobserve(element);
    });
  }, { root: null, rootMargin: '50% 0px', threshold: 0 });

  elements.forEach((el, idx) => {
    io.observe(el);
    debugLog(`Observing element ${idx}:`, el);
  });
}

/**
 * Initialize text animations (legacy - now handled by initScrollAnimations)
 */
function initTextAnimations() {
  // This function is now a no-op since text animation detection
  // is handled automatically in initScrollAnimations
}

/**
 * Initialize details element animations
 */
function initDetailsAnimations() {
  const detailsElements = document.querySelectorAll('details[data-animate-details]');

  detailsElements.forEach(details => {
    const content = details.querySelector('.details-content');
    if (!content) return;

    const animationType = details.getAttribute('data-animate-details') || DEFAULT_DETAILS_OPTIONS.type;
    const duration = parseFloat(details.getAttribute('data-animate-duration') || DEFAULT_DETAILS_OPTIONS.duration);
    const easing = details.getAttribute('data-animate-easing') || DEFAULT_DETAILS_OPTIONS.easing;

    const config = DETAILS_ANIMATIONS[animationType] || DETAILS_ANIMATIONS['blur-fade'];

    // Set initial closed state
    if (!details.open && config.open.from) {
      gsap.set(content, config.open.from);
    }

    // Add will-change for better performance
    content.style.willChange = 'opacity, transform, filter';

    // Add event listener for toggle events
    details.addEventListener('toggle', () => {
      if (details.open) {
        // Opening animation
        gsap.fromTo(content,
          config.open.from,
          {
            ...config.open.to,
            duration: duration,
            ease: easing
          }
        );
      } else {
        // Closing animation
        gsap.to(content, {
          ...config.close.to,
          duration: duration,
          ease: easing
        });
      }
    });
  });
}

/**
 * Cleanup animations within a root element (or entire document)
 * - Kills ScrollTriggers scoped to the root
 * - Removes inline styles added by GSAP where safe
 * - Unwraps any created split text spans
 */
function cleanupAnimations(root = document) {
  try {
    if (typeof ScrollTrigger !== 'undefined') {
      const triggers = ScrollTrigger.getAll();
      triggers.forEach(t => {
        const triggerEl = t && t.trigger;
        const shouldKill =
          !root ||
          !triggerEl ||
          root.contains(triggerEl) ||
          (root === document && triggerEl && !document.contains(triggerEl));

        if (shouldKill) {
          t.kill();
        }
      });
    }

    // Reset only elements we touched explicitly; avoid nuking author styles
    const toReset = [];
    root.querySelectorAll('[data-animate]').forEach(parent => {
      toReset.push(parent);
      parent.querySelectorAll('.split-line, .split-word').forEach(el => toReset.push(el));
    });
    toReset.forEach(el => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('filter');
      el.style.removeProperty('will-change');
    });

    // Unwrap split spans while preserving whitespace
    const unwrapAll = (selector) => {
      const nodes = Array.from(root.querySelectorAll(selector));
      nodes.forEach(node => {
        const parent = node.parentNode;
        if (!parent) return;
        const textNode = document.createTextNode(node.textContent);
        parent.replaceChild(textNode, node);
        parent.normalize();
      });
    };
    unwrapAll('.split-line');
    unwrapAll('.split-word');
  } catch (e) {
    console.warn('cleanupAnimations error', e);
  }
}

/**
 * Initialize animations scoped to a specific root
 */
function initializeAnimationsInRoot(root = document, options = {}) {
  const refresh = options.refresh !== false; // default true
  if (!isGSAPReady()) return;

  // Query within root only
  const initScopedScrollAnimations = () => {
    const elements = root.querySelectorAll('[data-animate]');

    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        const animationType = element.getAttribute('data-animate');
        const animateTypeAttr = element.getAttribute('data-animate-type');
        const shouldSplit = !!animateTypeAttr && animateTypeAttr !== 'none';
        const animateType = animateTypeAttr || 'words';
        const options = getOptionsFromAttributes(element);

        debugLog('Element in view (scoped), starting animation:', {
          element,
          animationType,
          shouldSplit,
          visible: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio
        });

        const textElement = getTextElement(element);
        if (shouldSplit && textElement) {
          applyTextAnimation(textElement, animationType, options, animateType);
        } else {
          applyScrollAnimation(element, animationType, options);
        }
        observer.unobserve(element);
      });
    }, { root: null, rootMargin: '50% 0px', threshold: 0 });

    elements.forEach(el => io.observe(el));
  };

  const initScopedDetailsAnimations = () => {
    const detailsElements = root.querySelectorAll('details[data-animate-details]');
    detailsElements.forEach(details => {
      const content = details.querySelector('.details-content');
      if (!content) return;

      const animationType = details.getAttribute('data-animate-details') || DEFAULT_DETAILS_OPTIONS.type;
      const duration = parseFloat(details.getAttribute('data-animate-duration') || DEFAULT_DETAILS_OPTIONS.duration);
      const easing = details.getAttribute('data-animate-easing') || DEFAULT_DETAILS_OPTIONS.easing;
      const config = DETAILS_ANIMATIONS[animationType] || DETAILS_ANIMATIONS['blur-fade'];

      if (!details.open && config.open.from) {
        gsap.set(content, config.open.from);
      }

      content.style.willChange = 'opacity, transform, filter';

      // Avoid duplicating listeners by removing and re-adding
      details.removeEventListener('toggle', details.__gsapToggleHandler);
      const handler = () => {
        if (details.open) {
          gsap.fromTo(content, config.open.from, { ...config.open.to, duration, ease: easing });
        } else {
          gsap.to(content, { ...config.close.to, duration, ease: easing });
        }
      };
      details.__gsapToggleHandler = handler;
      details.addEventListener('toggle', handler);
    });
  };

  initScopedScrollAnimations();
  initScopedDetailsAnimations();

  if (typeof ScrollTrigger !== 'undefined' && refresh) {
    ScrollTrigger.refresh();
  }
}

if (supportsPageSwap) {
  window.addEventListener('pageswap', () => {
    resetNavigationReady();
    resolveNavigationReady();
  });
} else {
  window.addEventListener('pagehide', () => {
    resetNavigationReady();
    resolveNavigationReady();
  });
}

if ('onpagereveal' in window) {
  window.addEventListener('pagereveal', event => {
    const transition = event.viewTransition;

    const finalize = () => {
      resolveNavigationReady();
      if (typeof ScrollTrigger !== 'undefined') {
        requestAnimationFrame(() => ScrollTrigger.refresh());
      }
    };

    if (transition && typeof transition.finished?.then === 'function') {
      transition.finished.then(finalize).catch(finalize);
    } else {
      finalize();
    }
  });
} else {
  window.addEventListener('pageshow', () => {
    resolveNavigationReady();
    if (typeof ScrollTrigger !== 'undefined') {
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }
  });
}

/**
 * Initialize all animations
 */
function initializeAnimations() {
  if (!isGSAPReady()) {
    console.warn('GSAP not fully loaded yet - retrying...');
    // Retry after a short delay
    setTimeout(initializeAnimations, 50);
    return;
  }

  // Disable animations in Shopify theme editor
  if (window.Shopify && Shopify.designMode) {
    debugLog('Shopify design mode detected - animations disabled');

    // Function to open accordions and reset animation styles
    const setupDesignMode = () => {
      // Make all animated elements visible immediately and reset animation properties
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.style.visibility = 'visible';
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.filter = 'none';
      });

      // Open accordion-custom details elements in design mode for easier editing
      document.querySelectorAll('accordion-custom details').forEach(details => {
        details.setAttribute('open', '');
      });
    };

    // Initial setup
    setupDesignMode();

    // Watch for DOM changes and reopen accordions (handles hot reloads)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node or its children contain accordion-custom
            if (node.matches && node.matches('accordion-custom')) {
              node.querySelectorAll('details').forEach(details => {
                details.setAttribute('open', '');
              });
            } else if (node.querySelectorAll) {
              node.querySelectorAll('accordion-custom details').forEach(details => {
                details.setAttribute('open', '');
              });
            }

            // Handle animated elements
            if (node.matches && node.matches('[data-animate]')) {
              node.style.visibility = 'visible';
              node.style.opacity = '1';
              node.style.transform = 'none';
              node.style.filter = 'none';
            } else if (node.querySelectorAll) {
              node.querySelectorAll('[data-animate]').forEach(el => {
                el.style.visibility = 'visible';
                el.style.opacity = '1';
                el.style.transform = 'none';
                el.style.filter = 'none';
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return;
  }

  // Check for prefers-reduced-motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (prefersReducedMotion.matches) {
    debugLog('prefers-reduced-motion detected - animations disabled');
    return;
  }

  debugLog('GSAP loaded successfully - initializing animations');
  gsap.registerPlugin(ScrollTrigger);
  // SplitText loaded lazily only when needed

  // Wait for DOM, view transitions, and then initialize
  const startAnimations = () => {
    if (hasInitializedOnce) return;
    debugLog('Starting animations');
    hasInitializedOnce = true;
    initScrollAnimations();
    initTextAnimations();
    initDetailsAnimations();

    // Shopify Theme Editor integration: re-initialize animations when sections/blocks change
    if (window.Shopify && Shopify.designMode && !window.__gsapShopifyBound) {
      window.__gsapShopifyBound = true;

      const onSectionLoad = (evt) => {
        const root = evt.target || (evt.detail && evt.detail.sectionId && document.getElementById(`shopify-section-${evt.detail.sectionId}`));
        if (root) {
          cleanupAnimations(root);
          initializeAnimationsInRoot(root);
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.refresh();
          }

          // Reopen accordions in design mode after section load
          if (window.Shopify && Shopify.designMode) {
            root.querySelectorAll('details').forEach(details => {
              details.setAttribute('open', '');
            });
          }
        }
      };

      const onSectionUnload = (evt) => {
        const root = evt.target || (evt.detail && evt.detail.sectionId && document.getElementById(`shopify-section-${evt.detail.sectionId}`));
        if (root) {
          cleanupAnimations(root);
        }
      };

      const onSectionReorder = (evt) => {
        const root = document;
        cleanupAnimations(root);
        initializeAnimationsInRoot(root);
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }

        // Reopen all accordions in design mode after section reorder
        if (window.Shopify && Shopify.designMode) {
          document.querySelectorAll('details').forEach(details => {
            details.setAttribute('open', '');
          });
        }
      };

      const onBlockChange = (evt) => {
        // Do NOT cleanup on block select/deselect; only refresh triggers
        const root = evt.target || document;
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.getAll().forEach(t => {
            if (!root || (t.trigger && root.contains(t.trigger))) {
              t.refresh();
            }
          });
        }
      };

      document.addEventListener('shopify:section:load', onSectionLoad);
      document.addEventListener('shopify:section:unload', onSectionUnload);
      document.addEventListener('shopify:section:reorder', onSectionReorder);
      document.addEventListener('shopify:block:select', onBlockChange);
      document.addEventListener('shopify:block:deselect', onBlockChange);
    }
  };

  if (document.readyState === 'loading') {
    debugLog('DOM not ready, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => {
      debugLog('DOM ready, starting animations');
      startAnimations();
    }, { once: true });
  } else {
    debugLog('DOM already ready, starting animations');
    startAnimations();
  }
}

// Start initialization immediately (scripts are loaded synchronously)
initializeAnimations();

// Export for potential use in other scripts
window.GSAPAnimations = {
  initScrollAnimations,
  initTextAnimations,
  initDetailsAnimations,
  isGSAPReady,
  // Expose scoped APIs so components can init within a container without touching the whole page
  initializeAnimationsInRoot,
  cleanupAnimations,
  animations: ANIMATION_REGISTRY,
  detailsAnimations: DETAILS_ANIMATIONS
};
